import React, { useState } from 'react';
import { IAssistantMessage, IOptionValidationRule } from '../../../types';
import { ChatMessage } from '../../message-box';
import { FormCard, FieldConstraint } from './FormCard';
import { ConfirmUpdateModal } from '../../ConfirmUpdateModal';
import { Obj } from '@/types';
import {
    ExtractedDimensions,
    FireEgressWindowType,
    OPTION_TYPE_NUMERIC_VALUES,
} from '../../../utils/fireEgress';
import { OPTION_TYPE_CUSTOM_FRACTION } from '../../../constants';

interface FormMessageBublesProps {
    message: IAssistantMessage;
    validationRules: IOptionValidationRule[];
    handleSubmit: (questionId: string, formValues: Obj) => void;
    updateSubmit: (questionId: string, formValues: Obj) => void;
    isEditMode?: boolean;
    egressDimensions?: ExtractedDimensions;
    egressWindowType?: FireEgressWindowType;
    fieldConstraints?: Record<string, FieldConstraint>;
    isCoupleDimension?: boolean;
    isTrueArch?: boolean;
}

export const FormMessageBubles: React.FC<FormMessageBublesProps> = ({
    message,
    validationRules,
    handleSubmit: handleSubmitProp,
    updateSubmit: updateSubmitProp,
    isEditMode = false,
    egressDimensions,
    fieldConstraints,
    egressWindowType,
    isCoupleDimension = false,
    isTrueArch = false,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [pendingValues, setPendingValues] = useState<Obj>({});

    const hasAnswer = message.response?.form && Object.keys(message.response.form).length > 0;

    const isNumericValues = message.question?.optionType === OPTION_TYPE_NUMERIC_VALUES;
    const isCustomFraction = message.question?.optionType === OPTION_TYPE_CUSTOM_FRACTION;

    const handleSubmit = (values: Obj) => {
        if (hasAnswer && !isEditMode) {
            setPendingValues(values);
            setShowModal(true);
        } else if (hasAnswer && isEditMode) {
            updateSubmitProp(message.question!.questionId, values);
        } else {
            handleSubmitProp(message.question!.questionId, values);
        }
    };

    const handleConfirm = () => {
        if (Object.keys(pendingValues).length > 0) {
            updateSubmitProp(message.question!.questionId, pendingValues);
        }
        setShowModal(false);
        setPendingValues({});
    };

    const handleCancel = () => {
        setShowModal(false);
        setPendingValues({});
    };

    const formatFormValues = (formValues: Obj) => {
        return Object.entries(formValues)
            .filter(([key]) => !key.startsWith('__'))
            .map(([key, value]) => {
                const option = message.question!.options?.find(opt =>
                    opt.optionId === key || opt.responseValue === key
                );
                const label = option?.optionText || key;
                return `${label}: ${value}`;
            })
            .join(', ');
    };

    const oldAnswerText = message.response?.form ? formatFormValues(message.response.form) : '';
    const newAnswerText = formatFormValues(pendingValues);

    return (
        <div>
            <ChatMessage message={message} />
            <FormCard
                options={message.question!.options || []}
                onSubmit={handleSubmit}
                validationRules={validationRules}
                savedValues={message.response?.form}
                heightConstraint={isNumericValues && egressDimensions?.windowHeight
                    ? egressDimensions.windowHeight
                    : undefined}
                percentageSumMax={isCustomFraction ? 100 : undefined}
                fieldConstraints={fieldConstraints}
                isCoupleDimension={isCoupleDimension}
                isTrueArch={isTrueArch}
            />
            <ConfirmUpdateModal
                visible={showModal}
                questionText={message.question!.question}
                oldAnswer={oldAnswerText}
                newAnswer={newAnswerText}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </div>
    );
}
